import { UIElement } from '../ui-element';
import { Digest } from '../../global/index';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import type { ElementConfig } from '../ui-element.type';
import styles from './ui-definition-list-item.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIDefinitionListItem
 * @element ui-definition-list-item
 * @classdesc Represents a class for <code>ui-definition-list-item</code> element.
 * A single term-definition pair for <code>ui-definition-list</code>.
 * @property {string} [label] {@attr label} Term content. Ignored when the named <code>label</code> slot has content.
 * @slot {@name label} An alternative way to provide term content with HTML. If you need to have interactive elements such as buttons here, wrap term text into <code>ui-definition-list-term</code> to keep the term itself separated and more accessible.
 * @slot {@name trailing} A slot to pass content appearing after the definition, such as buttons or links.
 * @example
 * <ui-definition-list>
 *   <ui-definition-list-item label="Color">Green</ui-definition-list-item>
 * </ui-definition-list>
 * @example
 * <ui-definition-list>
 *   <ui-definition-list-item>
 *     <ui-definition-list-term slot="label">Color</ui-definition-list-term>
 *     Green
 *   </ui-definition-list-item>
 * </ui-definition-list>
 * @example
 * <ui-definition-list>
 *   <ui-definition-list-item>
 *     <span slot="label">
 *       <ui-definition-list-term>Color</ui-definition-list-term>
 *       <button>Click me</button>
 *     </span>
 *     Green
 *   </ui-definition-list-item>
 * </ui-definition-list>
 * @example
 * <ui-definition-list>
 *   <ui-definition-list-item label="Label">
 *     Definition
 *     <ui-link label="Edit" href="#" layout="icon" glyph="redesign-circle-pen" icon-position="left" slot="trailing"></ui-link>
 *   </ui-definition-list-item>
 * </ui-definition-list>
 */
@Component({
    tag: 'ui-definition-list-item',
    styles: styles,
})
export class UIDefinitionListItem extends UIElement {
    @Attribute(String) label?: string;

    /**
     * Creates trailing content wrapper for a trailing slot child.
     * @param {HTMLElement} child
     * @returns {ElementConfig}
     * @private
     */
    private createDefinitionTrailingWrapper(child: HTMLElement): ElementConfig {
        return {
            tagName: 'div',
            classList: {
                'ui-definition-list-item__value-trailing': true,
            },
            children: [child],
        };
    }

    /**
     * Resolves term id and rendered term element from label slot content.
     * @param {HTMLElement} labelSlotElement
     * @returns {object}
     * @property {string} termId - unique id for the term element, used as aria-labelledby target for the definition.
     * @property {ElementConfig|HTMLElement} termElement - the term element or its configuration object.
     * @private
     */
    private resolveTermFromSlot(labelSlotElement: HTMLElement): {
        termId: string;
        termElement: ElementConfig | HTMLElement;
    } {
        const termEl =
            labelSlotElement.localName === 'ui-definition-list-term'
                ? labelSlotElement
                : labelSlotElement.querySelector<HTMLElement>(
                      'ui-definition-list-term'
                  );

        if (termEl) {
            labelSlotElement.classList.add('ui-definition-list-item__label');

            const termId = termEl.getAttribute('id') ?? Digest.randomId();
            termEl.setAttribute('id', termId);

            return {
                termId,
                termElement: labelSlotElement,
            };
        }

        const termId = Digest.randomId();
        return {
            termId,
            termElement: this.createTermWrapper(termId, [labelSlotElement]),
        };
    }

    /**
     * Creates fallback term wrapper.
     * @param {string} termId
     * @param {Array<string | HTMLElement>} children
     * @returns {ElementConfig}
     * @private
     */
    private createTermWrapper(
        termId: string,
        children: Array<string | HTMLElement>
    ): ElementConfig {
        return {
            tagName: 'span',
            classList: {
                'ui-definition-list-item__label': true,
            },
            attributes: {
                id: termId,
                role: 'term',
            },
            children,
        };
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const [labelSlottedEl] = this.getChildrenForSlot('label');
        const [trailingEl] = this.getChildrenForSlot('trailing');
        const definitionSlottedEls = this.detachChildNodes();

        let termId = '';
        let termElement: HTMLElement | ElementConfig | null = null;

        if (labelSlottedEl) {
            const resolvedTerm = this.resolveTermFromSlot(labelSlottedEl);
            termId = resolvedTerm.termId;
            termElement = resolvedTerm.termElement;
        } else if (this.label) {
            termId = Digest.randomId();
            termElement = this.createTermWrapper(termId, [this.label]);
        }

        const trailingWrapperConfig: ElementConfig | null = trailingEl
            ? {
                  tagName: 'div',
                  classList: {
                      'ui-definition-list-item__value-trailing': true,
                  },
                  children: [trailingEl],
              }
            : null;

        const definitionContent: Array<ElementConfig | HTMLElement> =
            trailingWrapperConfig
                ? [...definitionSlottedEls, trailingWrapperConfig]
                : definitionSlottedEls;

        const definitionElConfig: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-definition-list-item__value': true,
            },
            attributes: {
                'role': termId ? 'definition' : null,
                'aria-labelledby': termId || null,
            },
            children: definitionContent,
        };

        this.insertElements([termElement, definitionElConfig].filter(Boolean));
    }
}
